import {
  CircleCheck,
  CircleX,
  CircleHelp,
  Star,
  Copy,
  TicketX,
  PencilLine,
} from 'lucide-react'
import { Event } from '@wellesley/openapi'
import { useCurrentUser } from '@wellesley/user'
import { Action } from 'components/ActionsDropdown'
import { Badge } from 'components/Badge'
import {
  usePostApiV1EventsByEventIdAcceptMutation,
  usePostApiV1EventsByEventIdRejectMutation,
  usePostApiV1EventsByEventIdMaybeMutation,
  usePostApiV1EventsByEventIdJoinMutation,
  usePostApiV1EventsByEventIdInterestedMutation,
  useDeleteApiV1EventsByEventIdJoinOrInterestedMutation,
} from '../../api'
import { AttendeeAction } from '../../components/AttendeeActions'
import { EventsContextType } from '../../helpers/Events.context'
import { EVENT_PRIVACY_OPTIONS, EventPrivacy } from '../../helpers/Events.utils'
import { useEventStartCounter } from '../useEventStartCounter'

type UseEventActionsType = EventsContextType & {
  event: Event
  withActiveTimer?: boolean
}

export const useEventActions = ({
  event,
  withActiveTimer,
  openEventDialog,
  openCancelDialog,
}: UseEventActionsType) => {
  const { user } = useCurrentUser()

  const [acceptRequest, { isLoading: isLoadingAccept }] =
    usePostApiV1EventsByEventIdAcceptMutation()
  const [rejectRequest, { isLoading: isLoadingReject }] =
    usePostApiV1EventsByEventIdRejectMutation()
  const [maybeRequest, { isLoading: isLoadingMaybe }] =
    usePostApiV1EventsByEventIdMaybeMutation()

  const [joinRequest, { isLoading: isLoadingJoin }] =
    usePostApiV1EventsByEventIdJoinMutation()
  const [interestedRequest, { isLoading: isLoadingInterested }] =
    usePostApiV1EventsByEventIdInterestedMutation()
  const [leaveRequest, { isLoading: isLoadingLeave }] =
    useDeleteApiV1EventsByEventIdJoinOrInterestedMutation()

  const { id, organizer, privacy, status, startTime, attendeeStatus } = event

  const isOrganizer = user?.id === organizer.id
  const isPublic = privacy === 'PUBLIC'
  const isInviteOnly = privacy === 'DIRECT'
  const isCancelled = status === 'CANCELLED'

  const { icon: PrivacyIcon, title: privacyTitle } =
    EVENT_PRIVACY_OPTIONS[privacy as EventPrivacy]

  const startingIn = useEventStartCounter({
    startTime,
    enabled: withActiveTimer,
  })
  const isInPast = !startingIn

  const showInviteOnlyAttendeesAction =
    !isOrganizer && isInviteOnly && !isCancelled && !isInPast

  const showPublicAttendeeActions =
    !isOrganizer && isPublic && !isCancelled && !isInPast

  let organizerActions: Action[] = []
  let attendeeActions: AttendeeAction[] = []

  // Actions of organizer
  if (isOrganizer) {
    organizerActions = [
      {
        name: 'Edit',
        icon: <PencilLine />,
        handler: () => openEventDialog({ event }),
        isHidden: isCancelled || isInPast,
      },
      {
        name: 'Cancel',
        icon: <TicketX />,
        handler: () => openCancelDialog({ event }),
        isHidden: isCancelled || isInPast,
      },
      {
        name: 'Copy as new',
        icon: <Copy />,
        handler: () => openEventDialog({ event, copyAsNew: true }),
      },
    ].filter((a) => !a.isHidden)
  }

  // Actions of invite-only event attendee
  if (showInviteOnlyAttendeesAction) {
    const disabled = isLoadingAccept || isLoadingReject || isLoadingMaybe

    attendeeActions = [
      {
        name: 'Yes',
        value: 'JOINED',
        icon: CircleCheck,
        isSelected: attendeeStatus === 'JOINED',
        handler: () => {
          if (attendeeStatus !== 'JOINED') {
            void acceptRequest({ eventId: id })
          }
        },
        disabled,
      },
      {
        name: 'No',
        value: 'REJECTED',
        icon: CircleX,
        isSelected: attendeeStatus === 'REJECTED',
        handler: () => {
          if (attendeeStatus !== 'REJECTED') {
            void rejectRequest({ eventId: id })
          }
        },
        disabled,
      },
      {
        name: 'Maybe',
        value: 'JOINED_TENTATIVELY',
        icon: CircleHelp,
        isSelected: attendeeStatus === 'JOINED_TENTATIVELY',
        handler: () => {
          if (attendeeStatus !== 'JOINED_TENTATIVELY') {
            void maybeRequest({ eventId: id })
          }
        },
        disabled,
      },
    ]
  }

  // Actions of public event attendee
  if (showPublicAttendeeActions) {
    const disabled = isLoadingJoin || isLoadingInterested || isLoadingLeave

    const leaveActionNames = {
      JOINED: 'Not going',
      JOINED_TENTATIVELY: 'Not interested',
    }

    attendeeActions = [
      {
        name: 'Interested',
        value: 'JOINED_TENTATIVELY',
        icon: Star,
        isSelected: attendeeStatus === 'JOINED_TENTATIVELY',
        handler: () => {
          if (attendeeStatus !== 'JOINED_TENTATIVELY') {
            void interestedRequest({ eventId: id })
          }
        },
        disabled,
      },
      {
        name: 'Going',
        value: 'JOINED',
        icon: CircleCheck,
        isSelected: attendeeStatus === 'JOINED',
        handler: () => {
          if (attendeeStatus !== 'JOINED') {
            void joinRequest({ eventId: id })
          }
        },
        disabled,
      },
    ]

    if (attendeeStatus) {
      attendeeActions.push({
        name: leaveActionNames[attendeeStatus as keyof typeof leaveActionNames],
        icon: CircleX,
        handler: () => {
          if (attendeeStatus) {
            void leaveRequest({ eventId: id })
          }
        },
        disabled,
      })
    }
  }

  const coverBadges = (
    <div className="absolute bottom-xs left-xs">
      {isInviteOnly && (
        <Badge variant="neutral">
          <PrivacyIcon size={16} /> {privacyTitle}
        </Badge>
      )}
    </div>
  )

  return {
    isOrganizer,
    isInviteOnly,
    isPublic,
    isCancelled,
    startingIn,
    isInPast,
    organizerActions,
    attendeeActions,
    coverBadges,
  }
}
